<script setup>
import { computed, defineEmits } from 'vue';
import { useAppStore } from '../store/app';
import utils from '../common/utils';

const props = defineProps({
  onNavigateToProduct: {
    type: Function,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['onEditProduct']);

const store = useAppStore();

/**
 * `useProduct` custom hook
 */
const productDescription = computed(() => {
  return !utils.isEmpty(props.product?.description) ? props.product?.description : utils.randomText;
});

/**
 * Get the number of active and inactive customers
 */
const showCustomersSubscriptionStatus = ({ product }) => {
  const customerStatus = store.productsWithCustomers.find((productWithUser) => {
    return productWithUser.productName === product.name;
  });

  if (utils.isEmpty(customerStatus)) {
    // Default return if the is no match
    return {
      productName: '',
      activeCustomers: 0,
      inactiveCustomers: 0,
    };
  }

  return customerStatus ?? {};
};

const onEditProductClick = () => {
  emit('onEditProduct', props.product);
};
</script>

<template>
  <v-card class="product mr-2" height="200">
    <div @click="onNavigateToProduct({ product: product })" style="cursor: pointer">
      <div class="d-flex align-center mb-2">
        <h3>{{ product?.name }}</h3>
        <v-spacer></v-spacer>
        <v-chip
          variant="outlined"
          size="small"
          :color="product.freeTrialAvailable ? 'success' : 'grey'"
          >Free Trial: {{ product.freeTrialAvailable ? product.daysFreeTrial : 0 }} days</v-chip
        >
      </div>

      <p
        style="cursor: pointer"
        class="product__description mb-4"
        title="Click to view subscriptions">
        {{ productDescription }}
      </p>
    </div>
    <v-spacer></v-spacer>
    <v-divider></v-divider>
    <div class="product__actions mt-4">
      <div
        v-bind="props"
        @click="onNavigateToProduct({ product: props.product })"
        style="cursor: pointer">
        <v-icon color="red-darken-1">mdi-heart</v-icon>
        <span
          >{{
            showCustomersSubscriptionStatus({ product: props.product })?.activeCustomers
          }}
          subs</span
        >
        <v-tooltip activator="parent" location="bottom">Number of active subscribers</v-tooltip>
      </div>
      <div @click="onNavigateToProduct({ product: props.product })" style="cursor: pointer">
        <v-icon color="grey">mdi-heart-broken</v-icon>
        <span>
          {{ showCustomersSubscriptionStatus({ product: props.product })?.inactiveCustomers }}
          subs</span
        >
        <v-tooltip activator="parent" location="bottom">Number of inactive subscribers</v-tooltip>
      </div>

      <div style="cursor: pointer" title="Edit DynamicOdds">
        <v-tooltip text="Edit" location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              @click="onEditProductClick"
              variant="outlined"
              size="small"
              color="warning darken-1"
              prepend-icon="mdi-pencil-outline">
              Edit
            </v-btn>
          </template>
        </v-tooltip>
      </div>
    </div>
  </v-card>
</template>

<style lang="scss" scoped>
.product {
  width: 400px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  height: 100%;
  min-height: 200px;
  border-radius: 10px;
  font-size: 16px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;

  &__sub-title {
    color: rgba(var(--v-theme-text-primary));
    font-weight: 600;
    margin-bottom: 5px;
  }

  &__title {
    color: rgba(var(--v-theme-text-secondary));
    margin-bottom: 10px;
  }

  &__description {
    color: rgba(var(--v-theme-text-primary));
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__actions {
    display: flex;
    justify-content: space-between;

    div {
      color: rgba(var(--v-theme-text-secondary));
      display: flex;
      align-items: center;

      i {
        margin-right: 5px;
        color: rgba(var(--v-theme-text-primary));
      }

      span {
      }
    }
  }
}
</style>
